import type { BeforeSendFn } from 'posthog-js';

import { scrubSensitiveUrlParamsDeep } from '@/lib/analytics/sensitive-url-params';

// Warmly ships its own bundled widget and tRPC client; its requests fail whenever a
// visitor's network, extension, or ad blocker refuses the vendor host.
const WARMLY_HOST_FRAGMENT = 'getwarmly.com';

// This app has no tRPC dependency, so any tRPC client error originates in a vendor bundle.
const TRPC_CLIENT_ERROR_TYPE = 'TRPCClientError';

// PostHog wraps these in "Non-Error promise rejection captured with value: ..." and
// "'Error' captured as exception with message: '...'", so match the distinctive body
// rather than anchoring to the start of the value.
const BROWSER_EXTENSION_MESSAGE_PATTERNS = [
  // Injected by an extension's page bridge; the ids and counts vary per invocation.
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // Raised when an extension messages a tab that the user already closed.
  /Invalid call to runtime\.sendMessage\(\)\./,
];

const RESIZE_OBSERVER_LOOP_MESSAGES = new Set([
  // Browsers use different text for the same ResizeObserver delivery condition.
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
]);

function isResizeObserverLoopMessage(value: unknown): boolean {
  // Chromium has emitted the first message both with and without a final period.
  return (
    typeof value === 'string' && RESIZE_OBSERVER_LOOP_MESSAGES.has(value.trim().replace(/\.$/, ''))
  );
}

function isResizeObserverLoopException(properties: Record<string, unknown>): boolean {
  // Current PostHog exception events store structured errors in $exception_list.
  const exceptionList = properties.$exception_list;
  if (
    Array.isArray(exceptionList) &&
    exceptionList.some(
      (exception) =>
        typeof exception === 'object' &&
        exception !== null &&
        isResizeObserverLoopMessage((exception as Record<string, unknown>).value),
    )
  ) {
    return true;
  }

  // Older events can expose only the denormalized $exception_values array.
  const exceptionValues = properties.$exception_values;
  return Array.isArray(exceptionValues) && exceptionValues.some(isResizeObserverLoopMessage);
}

function containsWarmlyHost(value: unknown): boolean {
  return typeof value === 'string' && value.includes(WARMLY_HOST_FRAGMENT);
}

function isBrowserExtensionMessage(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    BROWSER_EXTENSION_MESSAGE_PATTERNS.some((pattern) => pattern.test(value))
  );
}

function isThirdPartyException(exception: unknown): boolean {
  if (typeof exception !== 'object' || exception === null) {
    return false;
  }

  const { stacktrace, type, value } = exception as {
    stacktrace?: { frames?: unknown };
    type?: unknown;
    value?: unknown;
  };

  if (type === TRPC_CLIENT_ERROR_TYPE) {
    return true;
  }

  if (containsWarmlyHost(value) || isBrowserExtensionMessage(value)) {
    return true;
  }

  // The widget bundle name is content-hashed, so match the vendor host on the frame URL
  // instead. PostHog renames `filename` to `source` during ingestion, so check both.
  const frames = stacktrace?.frames;
  return (
    Array.isArray(frames) &&
    frames.some((frame) => {
      if (typeof frame !== 'object' || frame === null) {
        return false;
      }
      const { filename, source } = frame as Record<string, unknown>;
      return containsWarmlyHost(filename) || containsWarmlyHost(source);
    })
  );
}

function isThirdPartyNoiseException(properties: Record<string, unknown>): boolean {
  const exceptionList = properties.$exception_list;
  if (Array.isArray(exceptionList) && exceptionList.some(isThirdPartyException)) {
    return true;
  }

  // Older events can expose only the denormalized type/value arrays.
  const exceptionTypes = properties.$exception_types;
  if (Array.isArray(exceptionTypes) && exceptionTypes.includes(TRPC_CLIENT_ERROR_TYPE)) {
    return true;
  }

  const exceptionValues = properties.$exception_values;
  return (
    Array.isArray(exceptionValues) &&
    exceptionValues.some((value) => containsWarmlyHost(value) || isBrowserExtensionMessage(value))
  );
}

/**
 * Removes browser ResizeObserver delivery diagnostics from PostHog error tracking.
 *
 * A browser reports this condition on `window` when a ResizeObserver callback
 * changes layout and leaves more observations for a later rendering frame. The
 * notification is synthetic and stackless because no application callback throws
 * it. As a result, PostHog groups normal layout activity from unrelated pages into
 * one apparently unhandled exception with no actionable application frame.
 *
 * Filtering the two standardized messages here preserves ResizeObserver behavior
 * and keeps every other exception visible. A reproducible layout bug should still
 * be fixed in its observer callback; this filter only removes the browser's generic
 * delivery diagnostic from telemetry.
 *
 * The same treatment applies to two families that originate outside this codebase:
 *
 * 1. The third-party Warmly visitor-identification widget. It loads its own bundle and
 *    tRPC client from `getwarmly.com` and calls `opps-api.getwarmly.com` directly, so a
 *    blocked or offline vendor host surfaces as an unhandled `Failed to fetch` that no
 *    change here can prevent or retry. PostHog marks the widget's frames `in_app`, which
 *    is why it reaches error tracking as if it were application code. This app has no
 *    tRPC dependency, so `TRPCClientError` identifies the vendor even when the exception
 *    carries neither the host nor a resolvable frame. Sentry already drops this family in
 *    `clientBeforeSend`; this keeps PostHog consistent with it.
 * 2. Browser-extension bridges injected into the page. They report against `window` with
 *    no application frame, so they group as large stackless issues that no deploy fixes.
 */
export const filterPostHogClientEvent: BeforeSendFn = (event) => {
  if (event?.event !== '$exception') {
    return event;
  }

  if (
    isResizeObserverLoopException(event.properties) ||
    isThirdPartyNoiseException(event.properties)
  ) {
    return null;
  }

  return event;
};

/**
 * Removes credential and sign-in code values from every URL an event carries, wherever posthog put
 * it: `$current_url`, `$referrer`, `$session_entry_url`, the `$initial_*` person properties in
 * `$set_once`, the per-metric objects of web vitals, autocapture element hrefs, exception frames.
 * It scrubs values rather than a list of property names, so properties posthog adds later are
 * covered too.
 *
 * Replay batches (`$snapshot`) pass through unchanged. They are large, and the recorder already
 * sends their URLs through `SESSION_REPLAY_MASKING.maskCapturedNetworkRequestFn`.
 */
export const scrubPostHogEventUrls: BeforeSendFn = (event) => {
  if (!event || event.event === '$snapshot') {
    return event;
  }

  return {
    ...event,
    properties: scrubSensitiveUrlParamsDeep(event.properties),
    ...(event.$set ? { $set: scrubSensitiveUrlParamsDeep(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubSensitiveUrlParamsDeep(event.$set_once) } : {}),
  };
};

/** The `before_send` chain for every event. It drops the noise, then scrubs what is left. */
export const POSTHOG_BEFORE_SEND: BeforeSendFn[] = [
  filterPostHogClientEvent,
  scrubPostHogEventUrls,
];
